import React from 'react';
import { Button } from '@habitech/call-to-action';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import SkeletonLoader from 'src/components/common/skeletonLoading';
import Icon from 'src/components/common/Icon';
import { getPanelTransactions } from 'src/redux/panel_transactions/thunks';
import {
    PAGE_SIZE_OPTIONS,
    PAYMENT_METHOD_LABELS,
    PAYMENT_METHOD_OPTIONS,
    PAYMENT_STATUS_LABELS,
} from 'src/redux/panel_transactions/panel_transactions.constants';
import {
    selectPanelTransactionsError,
    selectPanelTransactionsHistoryFrom,
    selectPanelTransactionsItems,
    selectPanelTransactionsLoading,
    selectPanelTransactionsQuery,
    selectPanelTransactionsSummary,
    selectPanelTransactionsTotal,
} from 'src/redux/panel_transactions/panel_transactions.selectors';
import {
    PanelPaymentMethod,
    PanelPaymentStatus,
    PanelTransactionScope,
    PanelTransactionsQuery,
} from 'src/redux/panel_transactions/panel_transactions.types';
import Pagination from '../Assignments/Pagination';
import { useLazyPanelTransactions } from '../hooks/useLazyPanelTransactions';
import TransactionsTable from './TransactionsTable';
import TransactionSearch from './TransactionSearch';
import CustomerEmailSearch from './CustomerEmailSearch';
import FilterSelect, { FilterSelectOption } from './FilterSelect';
import { buildPeriodOptions, buildTransactionsColumnLayout, formatAmount, formatHistoryFrom, formatTransactionCode, totalSummaryAmount } from './Transactions.helpers';
import {
    Card,
    CardFooter,
    CardHeader,
    ErrorState,
    FiltersRow,
    FooterPager,
    HistoryNote,
    LoadingBar,
    LookupNotice,
    NewBadge,
    ReadOnlyTag,
    ScopeOption,
    ScopeSelector,
    TableArea,
    TransactionsRoot,
} from './Transactions.styles';

const READ_ONLY_COPY = 'Solo lectura en este incremento';

/**
 * "Mías" es el alcance de cualquiera que vea la pestaña; "Todas" exige el permiso
 * `transacciones_todas`.
 *
 * `team` existe en el contrato del API (y responde 403), pero NO se ofrece acá:
 * no lo bloquea un permiso que alguien pueda pedir, sino que no hay estructura
 * de equipos con la que resolverlo. Una opción deshabilitada para siempre no
 * informa, promete. Cuando existan los equipos se agrega la opción.
 */
const SCOPE_OPTIONS: { value: PanelTransactionScope; label: string }[] = [
    { value: 'mine', label: 'Mías' },
    { value: 'all', label: 'Todas' },
];

const isScopeEnabled = (scope: PanelTransactionScope, canSeeAll: boolean) =>
    scope === 'mine' || canSeeAll;

const SCOPE_DISABLED_HINT = 'Disponible según permiso';

/**
 * El texto tiene que decir qué se está listando, sin repetir el alcance: Mías /
 * Todas ya está en el selector. Con un cliente, el correo va acá para que la
 * pestaña se lea sola. Con un número, el conteo nombra ese TX.
 */
const buildCountCopy = (
    total: number,
    customer: string,
    transactionId: number | null,
): string => {
    const noun = total === 1 ? 'operación' : 'operaciones';
    if (transactionId !== null) {
        const results = total === 1 ? 'resultado' : 'resultados';
        return `${total} ${results} para ${formatTransactionCode(transactionId)}`;
    }
    if (customer) {
        return `${total} ${noun} de ${customer}`;
    }
    return `${total} ${noun}`;
};

/**
 * El botón sale del cliente hacia el listado sin filtrar, que con alcance global
 * no es la cartera de quien mira sino la operación completa: ofrecer "mi
 * cartera" ahí prometería un destino que no es el que abre.
 */
const buildPortfolioCopy = (isGlobalScope: boolean): string =>
    isGlobalScope ? 'Ver todo el listado' : 'Ver toda mi cartera';

const buildEmptyCopy = (
    isCustomerView: boolean,
    isGlobalScope: boolean,
    transactionId: number | null,
): string => {
    /**
     * El vacío de una búsqueda por número es ambiguo: la transacción puede no
     * existir o puede existir y no ser del comercial que pregunta, porque el
     * recorte por alcance sigue aplicando. Decir "no existe" sería afirmar algo
     * que la respuesta no permite afirmar, así que el texto se limita a lo que
     * se sabe y el aviso de arriba ofrece ampliar el alcance.
     */
    if (transactionId !== null) {
        const code = formatTransactionCode(transactionId);
        return isGlobalScope
            ? `No encontramos ${code} en el histórico disponible. Revisa el número.`
            : `No encontramos ${code} entre tus operaciones.`;
    }
    if (isCustomerView) {
        return isGlobalScope
            ? 'Este cliente no tiene operaciones en el periodo seleccionado.'
            : 'No le has vendido nada a este cliente en el periodo seleccionado.';
    }
    return isGlobalScope
        ? 'No hay operaciones en el periodo seleccionado.'
        : 'No tienes operaciones en el periodo seleccionado.';
};

/** Los filtros no se aplican con un número en la mano; el motivo va en su tooltip. */
const FILTER_DISABLED_HINT =
    'No aplica en una búsqueda por número de transacción: el número se busca en todo el histórico disponible.';

/** "" es "sin filtrar": el API omite el parámetro y no recorta por ese campo. */
const STATUS_FILTER_OPTIONS: FilterSelectOption<PanelPaymentStatus | ''>[] = [
    { value: '', label: 'Todos' },
    ...(Object.keys(PAYMENT_STATUS_LABELS) as PanelPaymentStatus[]).map((status) => ({
        value: status,
        label: PAYMENT_STATUS_LABELS[status],
    })),
];

/**
 * El filtro ofrece las mismas pasarelas que el carrito comercial. El backend
 * traduce cada una a las pasarelas (y, en OpenPay, a las formas que
 * históricamente se guardaron como pasarela propia y a la tarjeta 3DS del
 * carrito del usuario, que un administrador ve con alcance global).
 *
 * En OpenPay y MercadoPago la forma concreta (tarjeta, transferencia, depósito
 * en efectivo) no filtra: se ve en la celda "Monto y método".
 */
const METHOD_FILTER_OPTIONS: FilterSelectOption<PanelPaymentMethod | ''>[] = [
    { value: '', label: 'Todos' },
    ...PAYMENT_METHOD_OPTIONS.map((method) => ({
        value: method,
        label: PAYMENT_METHOD_LABELS[method],
    })),
];

/**
 * Pestaña "Transacciones": responde "¿se pagó esta orden?" sin salir de
 * /retention. Solo lectura.
 *
 * Dos estados según el filtro de correo DEL LISTADO (no el buscador central):
 *   - Estado A (hay correo): las operaciones de ese cliente, sin columna
 *     Cliente porque todas las filas son de la misma persona.
 *   - Estado B (correo vacío): la cartera, con columna Cliente.
 *
 * El buscador de /retention puede sembrar ese filtro; escribir aquí no cambia
 * carrito ni vigencias. El alcance NO depende del estado: es del operador, y
 * el selector aparece en los dos. El recorte lo aplica el servidor con el
 * usuario del token — buscar un cliente no amplía lo visible.
 */
const Transactions = () => {
    const dispatch = useAppDispatch();
    useLazyPanelTransactions();

    const transactions = useAppSelector(selectPanelTransactionsItems);
    const loading = useAppSelector(selectPanelTransactionsLoading);
    const error = useAppSelector(selectPanelTransactionsError);
    const total = useAppSelector(selectPanelTransactionsTotal);
    const summary = useAppSelector(selectPanelTransactionsSummary);
    const historyFrom = useAppSelector(selectPanelTransactionsHistoryFrom);
    const query = useAppSelector(selectPanelTransactionsQuery);
    const canSeeAllScopes = useAppSelector(
        (state) => state.permissions?.flags?.transacciones_todas ?? false,
    );

    const isCustomerView = query.customerEmail !== '';
    const isGlobalScope = query.scope === 'all';
    // Búsqueda por número: un tercer modo, transversal al cliente buscado.
    const isLookup = query.transactionId !== null;
    const historyLabel = formatHistoryFrom(historyFrom);
    // En Estado A se nombra al cliente: el filtro vive en la tarjeta y la
    // pestaña tiene que poder leerse sola.
    const countCopy = buildCountCopy(
        total,
        isCustomerView ? query.customerEmail : '',
        query.transactionId,
    );
    // Un vacío con alcance propio puede ser "no es tuya" y no "no existe": si
    // quien busca tiene permiso de alcance global, se le ofrece repetir la
    // búsqueda ampliada en lugar de dejarlo descubrir el selector de alcance.
    const canWidenSearch = isLookup && total === 0 && !isGlobalScope && canSeeAllScopes;
    const showCustomer = !isCustomerView || isLookup;
    const showOrigin = isGlobalScope;
    const columnLayout = buildTransactionsColumnLayout({ showCustomer, showOrigin });

    // Sin argumentos reintenta la consulta vigente (el thunk hereda del estado).
    const handleFilterChange = (patch?: Partial<PanelTransactionsQuery>) => {
        dispatch(getPanelTransactions(patch));
    };

    // El backend pagina 0-based; el componente Pagination es 1-based.
    const handleChangePage = (uiPage: number) => {
        dispatch(getPanelTransactions({ page: uiPage - 1 }));
    };

    return (
        <TransactionsRoot>
            <Card>
                <CardHeader
                    $template={columnLayout.template}
                    $stacked={isCustomerView && !isLookup}
                >
                    <div
                        className={
                            isCustomerView && !isLookup
                                ? 'heading heading-stacked'
                                : 'heading'
                        }
                    >
                        <span className="heading-icon">
                            <Icon icon="pcom-icon-payments" size={18} />
                        </span>
                        <div className="heading-title">
                            <h3>Órdenes y transacciones</h3>
                            <NewBadge>Nuevo</NewBadge>
                        </div>
                        <p
                            className={
                                isLookup || isCustomerView
                                    ? 'heading-metrics'
                                    : 'heading-metrics heading-metrics-with-sum'
                            }
                        >
                            <span data-id="transactions-count">{countCopy}</span>
                            {!isLookup && !isCustomerView && (
                                <span className="heading-dot" aria-hidden="true">
                                    ·
                                </span>
                            )}
                        </p>
                    </div>
                    {!isLookup && (
                        <div className="heading-amount-slot">
                            <span
                                className="heading-amount-label"
                                data-id="transactions-summary-label"
                            >
                                Suma:
                            </span>
                            <span className="heading-amount" data-id="transactions-summary">
                                {formatAmount(totalSummaryAmount(summary))}
                            </span>
                        </div>
                    )}
                    <div className="actions">
                        {/* En el modo búsqueda la salida es una sola, la del
                            aviso: dos botones que vuelven a listados distintos
                            se pisarían, y limpiar las dos cosas a la vez
                            dispararía dos consultas compitiendo. */}
                        {isCustomerView && !isLookup && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="small"
                                originType="pcom"
                                dataId="button-transactions-view-portfolio"
                                onClick={() => handleFilterChange({ customerEmail: '' })}
                            >
                                {buildPortfolioCopy(isGlobalScope)}
                            </Button>
                        )}
                        {/* La etiqueta vive acá y no entre los filtros: la fila
                            de filtros envuelve, y era lo primero en caerse a un
                            segundo renglón. */}
                        <ReadOnlyTag data-id="transactions-readonly-tag">
                            <Icon icon="pcom-icon-password-show" size={13} />
                            {READ_ONLY_COPY}
                        </ReadOnlyTag>
                    </div>
                </CardHeader>

                <FiltersRow>
                    <TransactionSearch
                        value={query.transactionId}
                        onSearch={(transactionId) => handleFilterChange({ transactionId })}
                        onClear={() => handleFilterChange({ transactionId: null })}
                    />

                    <CustomerEmailSearch
                        value={query.customerEmail}
                        onSearch={(customerEmail) => handleFilterChange({ customerEmail })}
                        onClear={() => handleFilterChange({ customerEmail: '' })}
                        disabled={isLookup}
                        disabledHint={FILTER_DISABLED_HINT}
                    />

                    {/* El alcance NO se apaga en el modo búsqueda: no es un
                        filtro sino un permiso, y sigue recortando qué números
                        se pueden encontrar. */}
                    <ScopeSelector role="group" aria-label="Alcance del listado">
                        {SCOPE_OPTIONS.map((option) => {
                            const enabled = isScopeEnabled(option.value, canSeeAllScopes);
                            return (
                                <ScopeOption
                                    key={option.value}
                                    type="button"
                                    data-id={`transactions-scope-${option.value}`}
                                    $active={query.scope === option.value}
                                    disabled={!enabled}
                                    title={enabled ? undefined : SCOPE_DISABLED_HINT}
                                    onClick={() => handleFilterChange({ scope: option.value })}
                                >
                                    {option.label}
                                </ScopeOption>
                            );
                        })}
                    </ScopeSelector>

                    <FilterSelect
                        dataId="transactions-filter-status"
                        icon="pcom-icon-filter"
                        label="Estado de pago:"
                        value={query.paymentStatus}
                        options={STATUS_FILTER_OPTIONS}
                        onChange={(paymentStatus) => handleFilterChange({ paymentStatus })}
                        disabled={isLookup}
                        disabledHint={FILTER_DISABLED_HINT}
                    />

                    <FilterSelect
                        dataId="transactions-filter-method"
                        icon="pcom-icon-credit-card"
                        label="Método:"
                        value={query.paymentMethod}
                        options={METHOD_FILTER_OPTIONS}
                        onChange={(paymentMethod) => handleFilterChange({ paymentMethod })}
                        disabled={isLookup}
                        disabledHint={FILTER_DISABLED_HINT}
                    />

                    <FilterSelect
                        dataId="transactions-filter-period"
                        icon="pcom-icon-calendar-empty"
                        ariaLabel="Periodo"
                        value={query.yearMonth}
                        options={buildPeriodOptions()}
                        onChange={(yearMonth) => handleFilterChange({ yearMonth })}
                        disabled={isLookup}
                        disabledHint={FILTER_DISABLED_HINT}
                    />
                </FiltersRow>

                {query.transactionId !== null && (
                    <LookupNotice data-id="transactions-lookup-notice">
                        <span className="notice-icon">
                            <Icon icon="pcom-icon-information-filled" size={13} />
                        </span>
                        Búsqueda por número: {formatTransactionCode(query.transactionId)}.
                        El periodo y los filtros no aplican.
                        <span className="notice-spacer" />
                        {canWidenSearch && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="small"
                                originType="pcom"
                                dataId="button-transactions-lookup-widen"
                                onClick={() => handleFilterChange({ scope: 'all' })}
                            >
                                Buscar en todas
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            originType="pcom"
                            dataId="button-transactions-lookup-clear"
                            onClick={() => handleFilterChange({ transactionId: null })}
                        >
                            Ver el listado
                        </Button>
                    </LookupNotice>
                )}

                {error && (
                    <ErrorState>
                        {error}
                        <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            originType="pcom"
                            dataId="button-transactions-retry"
                            onClick={() => handleFilterChange()}
                        >
                            Reintentar
                        </Button>
                    </ErrorState>
                )}

                {loading && <LoadingBar data-testid="transactions-loading-bar" />}

                {loading && transactions.length === 0 ? (
                    <div style={{ padding: '16px 20px' }}>
                        <SkeletonLoader width="100%" height="20px" count={5} />
                    </div>
                ) : (
                    <TableArea $loading={loading}>
                        <TransactionsTable
                            transactions={transactions}
                            // En una búsqueda por número la fila puede ser de
                            // cualquiera, incluso con un cliente en el buscador
                            // central: sin la columna se leería como suya.
                            showCustomer={showCustomer}
                            // Con alcance propio toda fila es propia: la columna
                            // sobraría y sugeriría que puede haber otra cosa.
                            showOrigin={showOrigin}
                            emptyCopy={buildEmptyCopy(
                                isCustomerView,
                                isGlobalScope,
                                query.transactionId,
                            )}
                        />
                    </TableArea>
                )}

                <CardFooter>
                    {historyLabel ? (
                        <HistoryNote data-id="transactions-history-note">
                            <Icon icon="pcom-icon-information-filled" size={13} />
                            Histórico disponible desde {historyLabel}
                        </HistoryNote>
                    ) : (
                        <span />
                    )}
                    {/* Una búsqueda por número devuelve una fila o ninguna: el
                        paginador y el "mostrar N por página" no tienen nada que
                        hacer ahí. */}
                    {total > 0 && !isLookup && (
                        <FooterPager>
                            <Pagination
                                dataId="transactions-pagination"
                                page={query.page + 1}
                                total={total}
                                pageSize={query.pageSize}
                                onPageChange={handleChangePage}
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                onPageSizeChange={(pageSize) =>
                                    handleFilterChange({ pageSize })
                                }
                            />
                        </FooterPager>
                    )}
                </CardFooter>
            </Card>
        </TransactionsRoot>
    );
};

export default Transactions;
